import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Loader2, Check, Sun, Droplets, Info } from 'lucide-react';
import type { Plant } from '../types/plant';
import { useGarden } from '../hooks/useGarden';
import { useAuth } from '../hooks/useAuth';
import GardenSuccessModal from './modals/GardenSuccessModal';
import GardenLoginModal from './modals/GardenLoginModal';

interface PlantCardProps {
  plant: Plant;
  isInGarden?: boolean;
  onClick?: () => void;
}

export default function PlantCard({ plant, isInGarden = false, onClick }: PlantCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToGarden, loading } = useGarden();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handleAddToGarden = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);
    
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    try {
      await addToGarden(plant.id);
      setShowSuccessModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to garden');
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onClick) {
      onClick();
    } else {
      navigate(`/plants/${plant.id}`, { state: { from: { type: 'plants' } } });
    }
  };

  const CardContent = () => (
    <>
      <div 
        className="relative w-full pt-[56.25%]"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className={`absolute inset-0 bg-gray-100 transition-opacity duration-300 ${imageLoaded ? 'opacity-0' : 'opacity-100'}`} />
        <img 
          src={plant.image_url} 
          alt={plant.name}
          onLoad={() => setImageLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover rounded-t-lg transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        />
        
        {/* Hover overlay with More Information button */}
        <div 
          className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-200 ${
            isHovering ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={handleClick}
            className="bg-white text-primary-600 px-4 py-2 rounded-md font-medium flex items-center gap-2 hover:bg-primary-50 transition-colors"
          >
            <Info className="h-4 w-4" />
            <span>More Information</span>
          </button>
        </div>
      </div>
      
      <div className="p-2 flex-1 flex flex-col">
        <h3 className="font-medium text-sm mb-1.5 line-clamp-1">{plant.name}</h3>
        
        <div className="grid grid-cols-2 gap-1 mb-2">
          <div className="flex items-center gap-1 text-xs">
            <Sun className="h-3 w-3 text-gray-600 flex-shrink-0" />
            <span className="truncate">{plant.sunlight_requirements}</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Droplets className="h-3 w-3 text-gray-600 flex-shrink-0" />
            <span className="truncate">{plant.water_requirements}</span>
          </div>
        </div>

        {error && (
          <p className="text-xs text-red-600 mb-1.5">{error}</p>
        )}

        <div className="mt-auto">
          {!isInGarden ? (
            <button
              onClick={handleAddToGarden}
              disabled={loading}
              className="w-full flex items-center justify-center gap-1 bg-primary-500 text-white px-2 py-1 rounded text-xs font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Plus className="h-3 w-3" />
                  <span>Add to Garden</span>
                </>
              )}
            </button>
          ) : (
            <div className="w-full flex items-center justify-center gap-1 bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
              <Check className="h-3 w-3" />
              <span>In Garden</span>
            </div>
          )}
        </div>
      </div>

      <GardenSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        plantName={plant.name}
        plantId={plant.id}
      />

      <GardenLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        plantName={plant.name}
      />
    </>
  );

  return (
    <div 
      onClick={handleClick}
      className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow h-full flex flex-col cursor-pointer"
    >
      <CardContent />
    </div>
  );
}